
/**
 * @file dcp/src/dcp-client/worker/supervisor2/delay-manager.js
 *
 * A support class for Supervisor2.
 * It is for managing delays and exponential backoff.
 *
 * @author Paul, paul@distributive.network
 * @date Feb-April 2023,
 * @module delay-manager
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

const debugging = require('dcp/debugging').scope('worker');
const dcp_timers = require('dcp/common/dcp-timers');
const { timeDilation } = require('./common');

/** @typedef {import('./index').Supervisor} Supervisor */

class DelayManager
{
  /**
   * @constructor
   * @param {Supervisor} supervisor
   * @param {number} delayIncrementMs
   */
  constructor(supervisor, delayIncrementMs)
  {
    /**
     * The default delay in ms, between calls to the functions
     *   sandbox.start, assign, work and supervisor.recordresult.
     * @todo XXXpfr Should these be taken from worker config?
     * @type {number}
     **/
    this.defaultDelayIncrementMs = delayIncrementMs;
    /** @type {Supervisor} */
    this.supervisor = supervisor;
    /**
     * Fine tune this.nextDelay.
     * @type {number}
     */
    this.delayScaler = 1.0;
    /**
     * The time in ms of the last call to the functions
     *   sandbox.start, assign, work and supervisor.recordResult.
     * @todo XXXpfr Should these be taken from worker config?
     * @type {Object<string, number>}
     **/
    this.lastDelayTime = {
      sandboxStart:  0,
      sandboxAssign: 0,
      sandboxWork:   0,
      recordResult:  0,
    };
    /**
     * Customize the delays in ms, between calls to the functions
     *   sandbox.start, assign, work and supervisor.recordresult.
     * @todo XXXpfr Should these be taken from worker config?
     * @type {Object<string, number>}
     **/
    this.delayIncrementMs = {
      sandboxStart:  this.defaultDelayIncrementMs,
      sandboxAssign: this.defaultDelayIncrementMs,
      sandboxWork:   this.defaultDelayIncrementMs,
      recordResult:  this.defaultDelayIncrementMs,
    };
  }
  /**
   * Get the next delay for key.
   * This function incrementally updates values in
   *   this.lastDelayTime = {
   *     sandboxStart:  0,
   *     sandboxAssign: 0,
   *     sandboxWork:   0,
   *     recordResult:  0,
   *   };
   * and then computes the appropriate delayMs and applies it to a promise timer.
   * 
   * nextDelay provides an approximate minimum time interval between calls to the following 4 functions
   *   sandboxStart, sandboxAssign, sandboxWork, recordResult.
   * The minimum time interval can be set for each usage scenario
   * E.g. this.delayIncrementMs['sandboxWork'] = newDelayMs ...
   *
   * @param {string} key - possible choices are in Object.keys(this.lastDelayTime)
   * @param {number} [timeMultiplier]
   * @returns {Promise<void>}
   */
  nextDelay(key, timeMultiplier)
  {
    if (timeMultiplier)
      timeMultiplier *= timeDilation;
    else
      timeMultiplier = timeDilation;

    /** @todo XXXpfr Should these be taken from worker config? */
    const fuzzBase = 50;
    const fuzzRandom = 100;

    const now = Date.now();
    this.lastDelayTime[key] += this.delayScaler * this.delayIncrementMs[key];
    let delayMs = 0;

    if (this.lastDelayTime[key] <= now)
      this.lastDelayTime[key] = now;
    else
      delayMs = this.lastDelayTime[key] - now;

    const delayDelta = Math.random() * fuzzRandom + fuzzBase;
    debugging('supervisor') && console.debug('nextDelay', key, timeMultiplier, delayMs, delayMs + delayDelta, this.lastDelayTime[key] - this.supervisor.lastTime);

    // Fuzz...
    delayMs += delayDelta; //Math.random() * fuzzRandom + fuzzBase;

    if (delayMs > 0)
      return new Promise(resolve => dcp_timers.setTimeout(resolve, timeMultiplier * delayMs));
    return Promise.resolve();
  }
}
exports.DelayManager = DelayManager;
